import { getNestedSubelementsByType } from '@fs/zion-sls'
import { getSharedElement } from './linkedPersonsHelpers'

/**
 * Checks if person has an element of the same type as the shared element
 * and returns a REM_SUB dispatch to remove it (to prevent duplicates)
 *
 * @param {Object} options
 * @param {Object} options.person - The person element to check
 * @param {Object} options.sharedElement - The shared element being linked
 * @returns {Object|null} REM_SUB dispatch if conflict found, null otherwise
 */
export const removeConflictingElementIfExists = ({ person, sharedElement }) => {
  if (!person || !sharedElement) return null

  const elementType = sharedElement?.fieldType || sharedElement?.elementType
  const conflictingElement = getNestedSubelementsByType(person, elementType, !!sharedElement?.fieldType)?.[0]

  if (!conflictingElement) return null

  // We cannot have two elements of the same type so we need to remove the existing one
  return {
    type: 'REM_SUB',
    value: {
      superPartitionKey: person?.partitionKey,
      subPartitionKey: conflictingElement?.partitionKey,
      superId: person.id,
      subId: conflictingElement.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Creates a dispatch to link a shared element to a person
 *
 * @param {Object} options
 * @param {Object} options.person - Person to link to
 * @param {Object} options.sharedElement - Element to link
 * @returns {Object} ADD_SUB dispatch object
 */
export const createLinkDispatch = ({ person, sharedElement }) => {
  return {
    type: 'ADD_SUB',
    value: {
      superPartitionKey: person?.partitionKey,
      subPartitionKey: sharedElement?.partitionKey,
      superId: person.id,
      subId: sharedElement.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Creates a dispatch to unlink an element from a person
 *
 * @param {Object} options
 * @param {Object} options.person - Person to unlink from
 * @param {Object} options.element - Element to unlink
 * @returns {Object} REM_SUB dispatch object
 */
export const createUnlinkDispatch = ({ person, element }) => {
  return {
    type: 'REM_SUB',
    value: {
      superPartitionKey: person?.partitionKey,
      subPartitionKey: element?.partitionKey,
      superId: person.id,
      subId: element.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * High-level function to create all dispatches needed to link a person to a shared element
 * Handles conflict detection and removal automatically
 *
 * @param {Object} options
 * @param {Object} options.person - Person to link
 * @param {Object} options.sharedElement - Element to share
 * @param {Array} options.images - SLS images state (not used here but kept for consistency)
 * @returns {Array<Object>} Array of dispatches [REM_SUB?, ADD_SUB]
 */
export const createLinkPersonDispatches = ({ person, sharedElement }) => {
  const dispatches = []

  if (!person || !sharedElement) return dispatches

  // Check for and remove conflicting element of same type
  const conflictRemovalDispatch = removeConflictingElementIfExists({ person, sharedElement })
  if (conflictRemovalDispatch) {
    dispatches.push(conflictRemovalDispatch)
  }

  // Add the link dispatch
  dispatches.push(createLinkDispatch({ person, sharedElement }))

  return dispatches
}

/**
 * High-level function to create dispatches needed to unlink a person from a shared element
 * Handles AGE special case (element-level vs field-level unlinking)
 *
 * AGE fields are shared at the element level (not the field level), so when unlinking
 * we need to remove the AGE element itself, not the AGE field.
 *
 * @param {Object} options
 * @param {Object} options.person - Person to unlink
 * @param {string} options.fieldId - ID of the field/element being unlinked
 * @param {string} options.fieldKey - Field type key (e.g., 'AGE', 'SEX_CODE', 'RACE_OR_COLOR')
 * @param {string} options.partitionKey - Partition key for the field
 * @param {Array} options.images - SLS images state
 * @param {Object} [options.sharedElement] - The shared AGE element, for a new Age that has no field to look it up from
 * @returns {Array<Object>} Array of unlink dispatches
 */
export const createUnlinkPersonDispatches = ({ person, fieldId, fieldKey, partitionKey, images, sharedElement }) => {
  const dispatches = []

  if (!person) return dispatches

  // Special case: AGE is shared at the element level, not field level
  if (fieldKey === 'AGE') {
    const sharedAgeElement = sharedElement || getSharedElement(images, fieldId)
    if (sharedAgeElement) {
      dispatches.push(
        createUnlinkDispatch({
          person,
          element: sharedAgeElement,
        })
      )
    }
  } else {
    // Standard fields (SEX_CODE, RACE_OR_COLOR) are attached at the PERSON level
    // We can remove them from the person directly using the field ID
    dispatches.push({
      type: 'REM_SUB',
      value: {
        superPartitionKey: person?.partitionKey,
        subPartitionKey: partitionKey,
        superId: person.id,
        subId: fieldId,
        elementType: 'PERSON',
      },
    })
  }

  return dispatches
}
